import type { IndicatorSeries } from "../types";
import type { HousingPolicyBundle } from "./housingPolicyRegimes";
import type { LegacyMortgageStressBundle } from "./legacyMortgageStress";
import { latestChange, latestValue, unitLabel } from "../utils/series.ts";

export type SituationTone = "neutral" | "up" | "down" | "mixed" | "na";

export interface SituationItem {
  id: string;
  title: string;
  headline: string;
  explanation: string;
  tone: SituationTone;
  evidence?: string;
  stale?: boolean;
}

function signed(value: number, digits = 1) {
  return `${value > 0 ? "+" : ""}${value.toFixed(digits)}`;
}


function isStale(...series: IndicatorSeries[]) {
  return series.some((s) => s.source.quality === "stale");
}

function latestNumericDate(series: IndicatorSeries) {
  return [...series.observations].reverse().find((o) => o.value != null)?.date ?? null;
}

function datedEvidence(series: IndicatorSeries, text: string) {
  const date = latestNumericDate(series);
  return date ? `${text} · ${date}` : text;
}

function recentDelta(series: IndicatorSeries, lookbackNumeric = 20) {
  const values = series.observations.filter((o) => o.value != null);
  if (values.length < 2) return null;
  const latest = values.at(-1)!;
  const prior = values[Math.max(0, values.length - 1 - lookbackNumeric)];
  return {
    value: (latest.value as number) - (prior.value as number),
    from: prior.date,
    to: latest.date
  };
}

function noData(id: string, title: string, explanation: string): SituationItem {
  return {
    id,
    title,
    headline: "현재 해석할 데이터가 부족합니다.",
    explanation,
    tone: "na"
  };
}

export function buildSituationItems(input: {
  policyGap: IndicatorSeries;
  curve103: IndicatorSeries;
  fundingMomentum: IndicatorSeries;
  creditSpread: IndicatorSeries;
  mortgageFundingSpread: IndicatorSeries;
  corpLoanFundingSpread: IndicatorSeries;
  economicSentiment: IndicatorSeries;
  industrialProduction: IndicatorSeries;
  unsoldTotal: IndicatorSeries;
  housing?: HousingPolicyBundle;
  legacyMortgage?: LegacyMortgageStressBundle;
}): SituationItem[] {
  const out: SituationItem[] = [];

  const policyGap = latestValue(input.policyGap);
  if (policyGap == null) {
    out.push(noData("market", "시장금리", "국고채 3년과 기준금리가 모두 있어야 비교할 수 있습니다."));
  } else if (policyGap > 0) {
    out.push({
      id: "market",
      title: "시장금리",
      headline: `국고채 3년이 기준금리보다 ${policyGap.toFixed(1)}bp 높습니다.`,
      explanation: "중기 시장금리가 현재 정책금리보다 높은 상태입니다. 시장이 더 높은 중기 금리환경을 가격에 반영하고 있다는 뜻이지만, 추가 기준금리 인상을 확정적으로 뜻하지는 않습니다.",
      tone: "up",
      evidence: datedEvidence(input.policyGap, `Policy gap ${signed(policyGap)}bp`),
      stale: isStale(input.policyGap)
    });
  } else if (policyGap < 0) {
    out.push({
      id: "market",
      title: "시장금리",
      headline: `국고채 3년이 기준금리보다 ${Math.abs(policyGap).toFixed(1)}bp 낮습니다.`,
      explanation: "중기 시장금리가 현재 정책금리보다 낮은 상태입니다. 시장이 향후 더 낮은 금리환경을 일부 반영할 때 나타날 수 있지만, 기준금리 인하를 보장하는 신호는 아닙니다.",
      tone: "down",
      evidence: datedEvidence(input.policyGap, `Policy gap ${signed(policyGap)}bp`),
      stale: isStale(input.policyGap)
    });
  } else {
    out.push({ id: "market", title: "시장금리", headline: "국고채 3년과 기준금리의 차이가 거의 없습니다.", explanation: "정책금리와 중기 시장금리가 비슷한 수준에 위치한 상태입니다.", tone: "neutral", evidence: datedEvidence(input.policyGap, "Policy gap 0bp"), stale: isStale(input.policyGap) });
  }

  const curve = latestValue(input.curve103);
  if (curve != null) {
    out.push({
      id: "curve",
      title: "금리곡선",
      headline: curve >= 0
        ? `10년 금리가 3년보다 ${Math.abs(curve).toFixed(1)}bp 높습니다.`
        : `10년 금리가 3년보다 ${Math.abs(curve).toFixed(1)}bp 낮습니다.`,
      explanation: curve >= 0
        ? "장기금리가 중기금리보다 높은 정상적인 기울기 방향입니다. 기울기의 확대·축소 속도까지 함께 보는 것이 중요합니다."
        : "10년-3년 구간이 역전된 상태입니다. 역전 자체만으로 경기침체를 판정하지 않고, 신용·대출·실물계열로 실제 전이가 이어지는지 확인합니다.",
      tone: curve >= 0 ? "neutral" : "mixed",
      evidence: datedEvidence(input.curve103, `10Y-3Y ${signed(curve)}bp`),
      stale: isStale(input.curve103)
    });
  }

  const funding = latestValue(input.fundingMomentum);
  if (funding == null) {
    out.push(noData("funding", "은행 조달", "금융채 신규취급금리와 잔액금리가 모두 있어야 새 조달비용과 기존 평균비용을 비교할 수 있습니다."));
  } else {
    out.push({
      id: "funding",
      title: "은행 조달",
      headline: funding > 0
        ? `새 금융채 조달비용이 기존 잔액 평균보다 ${funding.toFixed(1)}bp 높습니다.`
        : funding < 0
          ? `새 금융채 조달비용이 기존 잔액 평균보다 ${Math.abs(funding).toFixed(1)}bp 낮습니다.`
          : "새 금융채 조달비용과 기존 잔액 평균이 비슷합니다.",
      explanation: funding > 0
        ? "새로 조달하는 자금이 기존 평균보다 비싸므로, 시간이 지나면서 은행의 누적 조달비용이 위로 따라갈 압력이 있습니다. 실제 대출금리가 얼마나 따라가는지는 다음 단계에서 별도로 확인합니다."
        : funding < 0
          ? "새 조달비용이 기존 평균보다 낮아 조달여건은 완화 방향입니다. 다만 기존 잔액이 천천히 교체되므로 실제 대출금리 완화는 늦을 수 있습니다."
          : "신규와 기존 조달비용의 방향 차이가 크지 않은 상태입니다.",
      tone: funding > 0 ? "up" : funding < 0 ? "down" : "neutral",
      evidence: datedEvidence(input.fundingMomentum, `신규-잔액 ${signed(funding)}bp`),
      stale: isStale(input.fundingMomentum)
    });
  }

  const credit = latestValue(input.creditSpread);
  const creditRecent = recentDelta(input.creditSpread, 20);
  const creditItem: SituationItem = credit == null
    ? noData("credit", "기업 신용", "회사채 AA-와 국고채 3년이 모두 있어야 신용스프레드를 계산할 수 있습니다.")
    : (() => {
        const direction = creditRecent == null || creditRecent.value === 0
          ? "최근 방향은 뚜렷하지 않습니다."
          : creditRecent.value > 0
            ? `최근 20개 유효 관측에서 ${creditRecent.value.toFixed(1)}bp 확대됐습니다.`
            : `최근 20개 유효 관측에서 ${Math.abs(creditRecent.value).toFixed(1)}bp 축소됐습니다.`;
        return {
          id: "credit",
          title: "기업 신용",
          headline: `AA- 회사채가 국고채 3년보다 ${credit.toFixed(1)}bp 높은 금리를 요구받고 있습니다.`,
          explanation: `${direction} 스프레드 확대는 기업 신용위험·유동성 프리미엄이 커지는 방향, 축소는 완화 방향으로 읽되 절대 임계값만으로 위기를 판정하지 않습니다.`,
          tone: creditRecent == null ? "neutral" : creditRecent.value > 0 ? "up" : creditRecent.value < 0 ? "down" : "neutral",
          evidence: datedEvidence(input.creditSpread, `Credit spread ${credit.toFixed(1)}bp`),
          stale: isStale(input.creditSpread)
        };
      })();

  const mortgageSpread = latestValue(input.mortgageFundingSpread);
  const corpSpread = latestValue(input.corpLoanFundingSpread);
  if (mortgageSpread == null && corpSpread == null) {
    out.push(noData("loan", "대출 전이", "신규 대출금리와 금융채 신규조달금리가 모두 연결되면 실제 차주에게 전달된 금리차를 볼 수 있습니다."));
  } else {
    const pieces: string[] = [];
    if (mortgageSpread != null) pieces.push(`주담대 ${signed(mortgageSpread)}bp`);
    if (corpSpread != null) pieces.push(`기업대출 ${signed(corpSpread)}bp`);
    out.push({
      id: "loan",
      title: "대출 전이",
      headline: `신규 금융채 조달금리 대비 ${pieces.join(" · ")}입니다.`,
      explanation: "이 차이는 실제 대출 pricing이 조달금리 위에 얼마나 위치하는지 보는 관찰값입니다. 신용위험·상품구성·운영비·은행 경쟁이 섞여 있으므로 은행의 순마진과 동일하지 않습니다.",
      tone: "neutral",
      evidence: datedEvidence(mortgageSpread != null ? input.mortgageFundingSpread : input.corpLoanFundingSpread, pieces.join(" / ")),
      stale: isStale(input.mortgageFundingSpread, input.corpLoanFundingSpread)
    });
  }

  const housing = input.housing;
  const legacyMortgage = input.legacyMortgage;
  if (housing && legacyMortgage) {
    const newMortgage = latestValue(legacyMortgage.inputs.newMortgageRate);
    const existingMortgage = latestValue(legacyMortgage.inputs.existingMortgageCost);
    const lockIn = latestValue(legacyMortgage.lockInSpread);
    const housingOutlook = latestValue(legacyMortgage.inputs.housingPriceOutlook);
    const capitalRows = housing.transactionModule.rtms?.scopes.capital ?? [];
    const availableHousingMonths = [...new Set(capitalRows.map((row) => row.month))].sort();
    const configuredFullMonth = housing.transactionModule.rtms?.coverage.latestFullCalendarMonth ?? null;
    const housingMonth = configuredFullMonth && availableHousingMonths.includes(configuredFullMonth)
      ? configuredFullMonth
      : availableHousingMonths.at(-1) ?? null;
    const previousHousingMonth = housingMonth ? (() => {
      const [y, m] = housingMonth.split("-").map(Number);
      const d = new Date(Date.UTC(y, m - 2, 1));
      return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
    })() : null;
    const transactionTotal = (month: string | null) => month == null ? null : capitalRows.filter((row) => row.month === month).reduce((sum, row) => sum + row.transactionCount, 0);
    const currentTransactions = transactionTotal(housingMonth);
    const previousTransactions = transactionTotal(previousHousingMonth);
    const transactionMom = currentTransactions != null && previousTransactions != null && previousTransactions > 0
      ? (currentTransactions / previousTransactions - 1) * 100
      : null;
    if (newMortgage == null && existingMortgage == null && housingOutlook == null && currentTransactions == null) {
      out.push(noData("housing", "주택 전이", "기존·신규 주담대 금리, 주택가격전망 CSI, 수도권 실거래가 연결되면 금융여건이 주택시장에 전달되는 경로를 별도로 확인합니다."));
    } else {
      const fragments: string[] = [];
      if (newMortgage != null && existingMortgage != null && lockIn != null) fragments.push(`신규 ${newMortgage.toFixed(2)}% / 기존 ${existingMortgage.toFixed(2)}% · gap ${signed(lockIn, 2)}%p`);
      else if (newMortgage != null) fragments.push(`신규 주담대 ${newMortgage.toFixed(2)}%`);
      if (housingOutlook != null) fragments.push(`주택가격전망CSI ${housingOutlook.toFixed(0)}`);
      if (currentTransactions != null) fragments.push(`수도권 거래 ${currentTransactions.toLocaleString()}건${transactionMom == null ? "" : ` (MoM ${signed(transactionMom, 1)}%)`}`);
      out.push({
        id: "housing",
        title: "주택 전이",
        headline: fragments.join(" · "),
        explanation: `신규 대출 flow와 기존 mortgage stock의 금리부담, 주택가격 기대, 실제 거래량을 분리해서 봅니다. ${legacyMortgage.inputs.mortgageBalance.source.quality === "unavailable" ? "동일 모집단의 예금은행 주담대 총잔액 Gate C가 없어 총 이자부담 규모와 Cash-flow Stress 자동분류는 아직 N/A입니다." : "총잔액 계열까지 연결된 경우에도 거래·가격과의 인과를 자동 판정하지 않습니다."}`,
        tone: "mixed",
        evidence: [legacyMortgage.snapshots.lockInSpread.latestMonth ? `Lock-in ${legacyMortgage.snapshots.lockInSpread.latestMonth}` : null, housingMonth ? `RTMS ${housingMonth}` : null].filter(Boolean).join(" · ") || undefined,
        stale: isStale(legacyMortgage.inputs.newMortgageRate, legacyMortgage.inputs.existingMortgageCost, legacyMortgage.inputs.housingPriceOutlook)
      });
    }

  }

  // Credit is a sibling transmission branch after actual loan pricing; keep the
  // beginner view ordered as market → funding → loans → housing / credit → real.
  out.push(creditItem);

  const esi = latestValue(input.economicSentiment);
  const esiChange = latestChange(input.economicSentiment);
  const productionChange = latestChange(input.industrialProduction);
  const unsoldChange = latestChange(input.unsoldTotal);
  if (esi == null && productionChange == null && unsoldChange == null) {
    out.push(noData("real", "실물 확인", "심리·생산·전국 미분양 데이터가 연결되면 금융여건 변화가 실물까지 전달됐는지 순서대로 확인합니다."));
  } else {
    const fragments: string[] = [];
    if (esi != null) fragments.push(`ESI ${esi.toFixed(1)}${esiChange == null ? "" : ` (${signed(esiChange, 1)} 최근 변화)`}`);
    if (productionChange != null) fragments.push(`생산지수 ${signed(productionChange, 1)} 최근 변화`);
    if (unsoldChange != null) fragments.push(`전국 미분양 ${signed(unsoldChange, 0)}호 최근 변화`);
    out.push({
      id: "real",
      title: "실물 확인",
      headline: fragments.join(" · "),
      explanation: "여기는 금융시장보다 늦게 반응하는 확인층입니다. 한 지표의 한 달 변화보다 심리→생산→주택재고가 같은 방향으로 이어지는지 시계열로 확인하는 용도입니다.",
      tone: "neutral",
      evidence: datedEvidence(input.economicSentiment.observations.some((o) => o.value != null) ? input.economicSentiment : input.industrialProduction, "최근 유효 관측 기준"),
      stale: isStale(input.economicSentiment, input.industrialProduction, input.unsoldTotal)
    });
  }

  return out;
}

export function metricHelp(series: IndicatorSeries, kind: "policy" | "market" | "policy_gap" | "curve" | "funding" | "credit") {
  const value = latestValue(series);
  if (kind === "policy") return "한국은행이 정하는 정책금리. 시장금리와 대출금리의 출발점입니다.";
  if (kind === "market") return "시장에서 거래되는 중기 금리. 정책금리보다 먼저 움직일 수 있습니다.";
  if (kind === "policy_gap") return value == null ? "국고채 3년과 기준금리의 차이입니다." : `국고채 3년이 기준금리보다 ${Math.abs(value).toFixed(1)}bp ${value >= 0 ? "높은" : "낮은"} 상태입니다.`;
  if (kind === "curve") return value == null ? "10년 금리와 3년 금리의 차이입니다." : `장기 10년 금리가 3년보다 ${Math.abs(value).toFixed(1)}bp ${value >= 0 ? "높습니다" : "낮습니다(역전)"}.`;
  if (kind === "funding") return value == null ? "새 금융채 조달비용과 기존 잔액 평균의 차이입니다." : `새 조달비용이 기존 평균보다 ${Math.abs(value).toFixed(1)}bp ${value >= 0 ? "높습니다" : "낮습니다"}.`;
  if (kind === "credit") return value == null ? "AA- 회사채와 국고채 3년의 금리차입니다." : `AA- 기업이 국채보다 추가로 부담하는 금리가 약 ${value.toFixed(1)}bp입니다.`;
  return `${series.name} (${unitLabel(series.unit)})`;
}

export type MetricTakeawayKind = "policy" | "market" | "policy_gap" | "curve" | "funding" | "credit" | "mortgage_stock" | "lock_in" | "housing_outlook";

export function metricTakeaway(series: IndicatorSeries, kind: MetricTakeawayKind) {
  const value = latestValue(series);
  const delta = latestChange(series);
  if (value == null) return "현재값을 해석할 데이터가 부족합니다.";
  const moved = delta == null || delta === 0 ? "직전 관측과 큰 변화가 없습니다." : delta > 0 ? "직전 관측보다 올라갔습니다." : "직전 관측보다 내려갔습니다.";

  if (kind === "policy") return `정책 자체의 현재 위치입니다. ${moved}`;
  if (kind === "market") return `실제 시장에서 먼저 움직일 수 있는 중기 금리입니다. ${moved}`;
  if (kind === "policy_gap") return value > 0
    ? "시장 3년 금리가 기준금리보다 위에 있어 중기 금융여건이 정책금리보다 높은 쪽입니다."
    : value < 0
      ? "시장 3년 금리가 기준금리보다 아래에 있어 완화 기대가 반영된 쪽입니다."
      : "시장 3년 금리와 기준금리가 거의 같은 수준입니다.";
  if (kind === "curve") return value < 0
    ? "10년-3년 구간이 역전돼 있습니다. 이것만으로 침체를 판정하지 않고 신용·실물 전이를 확인합니다."
    : "장기금리가 중기금리보다 높은 방향입니다. 기울기가 더 커지는지 줄어드는지를 함께 봅니다.";
  if (kind === "funding") return value > 0
    ? "은행이 새로 조달하는 비용이 기존 평균보다 비싸 누적 조달비용에 상방 압력이 있습니다."
    : value < 0
      ? "새 조달비용이 기존 평균보다 낮아 은행 조달여건은 완화 방향입니다."
      : "신규와 기존 조달비용의 차이가 거의 없습니다.";
  if (kind === "credit") return delta == null || delta === 0
    ? "기업이 국채보다 추가로 부담하는 신용 프리미엄의 현재 수준입니다."
    : delta > 0
      ? "신용 프리미엄이 직전 관측보다 넓어져 기업 조달여건은 압박 방향입니다."
      : "신용 프리미엄이 직전 관측보다 좁아져 기업 조달여건은 완화 방향입니다.";
  if (kind === "mortgage_stock") return delta == null || delta === 0
    ? "기존 차주가 실제로 부담하는 평균 주담대 금리의 현재 수준입니다."
    : delta > 0
      ? "기존 차주의 평균 주담대 금리가 올라 기존차주 부담이 커지는 방향입니다."
      : "기존 차주의 평균 주담대 금리가 내려 기존차주 부담이 줄어드는 방향입니다.";
  if (kind === "lock_in") return value > 0
    ? "새로 대출받는 금리가 기존차주 평균보다 높아 신규·이동 금융비용 격차가 있는 상태입니다."
    : value < 0
      ? "새로 대출받는 금리가 기존차주 평균보다 낮아 신규 금융비용 쪽은 완화된 상태입니다."
      : "신규와 기존 주담대 금리차가 거의 없습니다.";
  if (kind === "housing_outlook") return delta == null || delta === 0
    ? "주택가격 기대의 최근 방향이 크게 변하지 않았습니다."
    : delta > 0
      ? "주택가격에 대한 기대가 직전 관측보다 강해졌습니다. 실제 거래·가격과 같은 방향인지 확인해야 합니다."
      : "주택가격에 대한 기대가 직전 관측보다 약해졌습니다. 실제 거래·가격이 따라오는지 확인해야 합니다.";
  return moved;
}
